import { useState, useEffect, useRef } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import { toaster } from '@/shared/lib/toaster'
import {
  Box,
  Container,
  Heading,
  Text,
  Button,
  Grid,
  VStack,
  HStack,
  Badge,
  Input,
  Textarea,
  AccordionRoot,
  AccordionItem,
  AccordionItemTrigger,
  AccordionItemContent,
} from '@chakra-ui/react'
import { Field } from '@/components/ui/field'
import { NativeSelectRoot, NativeSelectField } from '@/components/ui/native-select'
import {
  Rocket,
  Target,
  Gift,
  Trophy,
  Phone,
  Users,
  ArrowRight,
  CheckCircle,
  ChevronDown,
  Sparkles,
  FolderOpen,
  Calculator,
  Vote,
  Globe,
  Clock,
  Zap,
  FileSpreadsheet,
  MessageSquare,
  Calendar,
  Star,
  Flame,
  Send,
  ArrowLeft,
  Check,
} from 'lucide-react'

// Nombre de places restantes (à synchroniser avec une vraie source de données plus tard)
const TOTAL_PLACES = 20
const PLACES_TAKEN = 3

// Pain Points data
const painPoints = [
  {
    icon: FileSpreadsheet,
    title: 'Ressaisir, exporter, réimporter... encore et encore',
    description:
      'Votre temps part dans des tâches sans valeur : copier des chiffres d\'un outil à l\'autre, exporter des tableaux pour les retravailler, vérifier que tout est cohérent entre vos 10 logiciels.',
    solution: 'Et si tout était connecté nativement ?',
  },
  {
    icon: Vote,
    title: "L'AG, c'est toujours le chaos",
    description:
      'Convocations papier (ou PDF bricolés), feuilles d\'émargement manuelles, votes à main levée, PV rédigé à la main puis retapé...',
    solution: "Et si l'AG était digitale de bout en bout ?",
  },
  {
    icon: Phone,
    title: 'Vos copropriétaires vous appellent pour tout',
    description:
      '"Où est mon décompte ?" "J\'ai perdu la convocation" "C\'est quoi le solde de mon compte ?"... Parce qu\'ils n\'ont pas d\'espace où trouver les réponses eux-mêmes.',
    solution: "Et s'ils avaient un extranet digne de ce nom ?",
  },
]

// Solution pillars data
const solutionPillars = [
  {
    icon: FolderOpen,
    title: 'GED Nouvelle Génération',
    description: 'Fini les documents éparpillés. Tout est centralisé, organisé, trouvable en 2 secondes.',
    features: ['Interface style Google Drive', 'Recherche instantanée', 'Partage sécurisé', 'Accessible partout'],
  },
  {
    icon: Calculator,
    title: 'Comptabilité 100% Intégrée',
    description: 'Fini les exports et ressaisies. La compta vit dans le même outil que tout le reste.',
    features: ['Plan comptable ALUR natif', 'Budgets prévisionnels', 'Appels de fonds auto', 'États financiers conformes'],
  },
  {
    icon: Vote,
    title: 'AG Digitalisée de A à Z',
    description: 'Fini le papier et les PV tapés à la main. L\'AG passe au 21ème siècle.',
    features: ['Convocations conformes auto', 'Votes par correspondance', 'Feuille de présence digitale', 'PV automatique'],
  },
  {
    icon: Globe,
    title: 'Extranet Copropriétaires',
    description: 'Fini les appels pour chaque question. Vos copropriétaires sont autonomes.',
    features: ['Accès documents 24/7', 'Consultation comptes', 'Historique des AG', 'Notifications auto'],
  },
]

// Founder advantages
const founderAdvantages = [
  {
    icon: Rocket,
    title: 'Digitalisation anticipée',
    description: 'Pendant que vos concurrents attendent, vous avez déjà tout en place',
  },
  {
    icon: Target,
    title: "Un outil qui s'adapte à vous",
    description: "Vos retours façonnent le produit — pas l'inverse",
  },
  {
    icon: Gift,
    title: "Gratuit jusqu'au lancement",
    description: 'Accès complet offert pendant tout le programme',
  },
  {
    icon: Star,
    title: '-30% à vie ensuite',
    description: 'Tarif Founder permanent quand vous serez convaincu',
  },
  {
    icon: Trophy,
    title: 'Statut de pionnier',
    description: 'Badge Founder, Mur des Founders, reconnaissance permanente',
  },
  {
    icon: Phone,
    title: 'Support prioritaire',
    description: 'Accès direct à l\'équipe fondatrice — pas de ticket niveau 1',
  },
]

// Founder commitments
const founderCommitments = [
  {
    icon: Users,
    title: 'Utiliser KOPRO pour de vrai',
    description: 'Au moins 1 copropriété gérée — pas juste "regarder"',
  },
  {
    icon: Vote,
    title: 'Faire au moins 1 AG sur KOPRO',
    description: 'C\'est le vrai test de la digitalisation',
  },
  {
    icon: MessageSquare,
    title: 'Partager vos retours',
    description: '45-60 min/semaine — ce qui manque, ce qui coince',
  },
  {
    icon: Calendar,
    title: 'Participer aux sessions',
    description: '2x/mois, 45 min — on améliore ensemble',
  },
  {
    icon: Star,
    title: 'Témoigner à la V1',
    description: 'Partagez votre parcours de digitalisation',
  },
]

// Timeline steps
const timelineSteps = [
  {
    step: 1,
    title: 'Candidature',
    duration: '3 min',
    description: 'Parlez-nous de votre activité et de ce qui vous freine aujourd\'hui dans votre digitalisation.',
  },
  {
    step: 2,
    title: 'Échange découverte',
    duration: '15 min',
    description: 'Discussion téléphonique pour faire connaissance et vérifier que le programme vous correspond.',
  },
  {
    step: 3,
    title: 'Onboarding personnalisé',
    duration: '1h',
    description: 'Session de configuration avec l\'équipe : votre compte est opérationnel, votre première copropriété importée.',
  },
  {
    step: 4,
    title: 'Digitalisation progressive',
    duration: "jusqu'à V1",
    description: 'GED, compta, puis votre première AG digitale. Extranet activé pour vos copropriétaires.',
  },
  {
    step: 5,
    title: 'Lancement V1',
    duration: 'T2 2025',
    description: 'Vous êtes déjà 100% digital. Les autres commencent à peine.',
  },
]

// FAQ data
const faqItems = [
  {
    question: "C'est vraiment utilisable ou c'est un prototype ?",
    answer:
      "KOPRO est un logiciel fonctionnel et complet. GED, comptabilité, AG, extranet — tout le cœur métier est là. Ce n'est pas un prototype, c'est un outil que vous pouvez utiliser pour travailler. Ce qu'on affine avec vous : les détails, les cas particuliers, les workflows spécifiques à votre réalité. Pas les fondations.",
  },
  {
    question: 'Qu\'est-ce qui manque concrètement ?',
    answer:
      'Les connexions automatiques avec des services externes : signature électronique (Yousign, DocuSign), recommandé électronique (AR24), paiements en ligne (Stripe, prélèvement SEPA), visioconférence intégrée. En attendant, ces fonctions se font "à côté" — KOPRO génère tous les documents nécessaires.',
  },
  {
    question: 'Combien de temps ça demande ?',
    answer:
      'Usage quotidien : variable selon votre activité (c\'est votre outil de travail). Retours structurés : ~15 min/semaine. Sessions d\'amélioration : 2x/mois, 45 min chacune. Total "en plus" de l\'usage normal : ~1h/semaine.',
  },
  {
    question: 'Je dois vraiment faire une AG sur KOPRO ?',
    answer:
      "Idéalement oui, dans les 6 premiers mois. L'AG digitalisée est un des gros apports de KOPRO, et votre retour d'expérience sera précieux. Mais ce n'est pas bloquant pour rejoindre — on adapte selon votre calendrier.",
  },
  {
    question: 'Et si ça ne me convient pas ?',
    answer:
      'Vous pouvez quitter le programme à tout moment. Vos données sont exportables. Pas de piège, pas d\'engagement forcé. Mais honnêtement ? On est confiants. Si vous cherchez à vous digitaliser, KOPRO va vous plaire.',
  },
  {
    question: "C'est vraiment gratuit ?",
    answer:
      "Jusqu'au lancement public (T2 2025), oui — accès complet. Ensuite, vous bénéficiez de -30% à vie sur l'abonnement de votre choix. C'est notre façon de remercier ceux qui nous aident à créer le meilleur outil possible.",
  },
  {
    question: 'Pourquoi seulement 20 places ?',
    answer:
      'Pour que ce soit un vrai partenariat, pas du support client. Avec 20 Founders, chaque retour est lu, chaque suggestion est intégrée. Au-delà, ce serait de la gestion de masse — ce n\'est pas l\'objectif du programme.',
  },
]

// Form steps
const formSteps = [
  { id: 1, title: 'Vous', fields: ['firstName', 'lastName', 'email', 'phone', 'role'] },
  { id: 2, title: 'Activité', fields: ['company', 'coprCount', 'lotsCount', 'currentTools'] },
  { id: 3, title: 'Digitalisation', fields: ['blockers', 'wishToAutomate', 'agPlanned', 'isDecider'] },
  { id: 4, title: 'Motivation', fields: ['whyNow', 'timeAvailable', 'source', 'referrer'] },
]

interface FormData {
  firstName: string
  lastName: string
  email: string
  phone: string
  role: string
  company: string
  coprCount: string
  lotsCount: string
  currentTools: string[]
  blockers: string
  wishToAutomate: string
  agPlanned: string
  isDecider: string
  whyNow: string
  timeAvailable: string
  source: string
  referrer: string
}

// Supabase Edge Function URL
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL || ''
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY || ''
const FOUNDER_APPLICATION_URL = `${SUPABASE_URL}/functions/v1/founder-application`

export function FounderProgramPage() {
  const [isFormOpen, setIsFormOpen] = useState(false)
  const [currentStep, setCurrentStep] = useState(1)
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [formData, setFormData] = useState<FormData>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    role: '',
    company: '',
    coprCount: '',
    lotsCount: '',
    currentTools: [],
    blockers: '',
    wishToAutomate: '',
    agPlanned: '',
    isDecider: '',
    whyNow: '',
    timeAvailable: '',
    source: '',
    referrer: '',
  })

  const formRef = useRef<HTMLDivElement>(null)

  // Places restantes
  const placesLeft = TOTAL_PLACES - PLACES_TAKEN
  const progressPercent = (PLACES_TAKEN / TOTAL_PLACES) * 100

  // Scroll to form when opening
  useEffect(() => {
    if (isFormOpen && formRef.current) {
      formRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }, [isFormOpen])

  const handleInputChange = (field: keyof FormData, value: string | string[]) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }

  const handleNextStep = async () => {
    if (currentStep < 4) {
      setCurrentStep(currentStep + 1)
    } else {
      // Submit form to edge function
      setIsSubmitting(true)
      try {
        const response = await fetch(FOUNDER_APPLICATION_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
            'apikey': SUPABASE_ANON_KEY,
          },
          body: JSON.stringify({
            firstName: formData.firstName,
            lastName: formData.lastName,
            email: formData.email,
            phone: formData.phone,
            role: formData.role,
            company: formData.company,
            coprCount: formData.coprCount,
            lotsCount: formData.lotsCount,
            currentTools: formData.currentTools,
            blockers: formData.blockers,
            wishToAutomate: formData.wishToAutomate,
            agPlanned: formData.agPlanned,
            isDecider: formData.isDecider,
            whyNow: formData.whyNow,
            timeAvailable: formData.timeAvailable,
            source: formData.source,
            referrer: formData.referrer,
          }),
        })

        if (!response.ok) {
          const errorData = await response.json()
          throw new Error(errorData.error || 'Une erreur est survenue')
        }

        setIsSubmitted(true)
      } catch (error) {
        console.error('Error submitting application:', error)
        toaster.error({
          title: 'Erreur',
          description: error instanceof Error ? error.message : 'Une erreur est survenue lors de l\'envoi de votre candidature. Veuillez réessayer.',
        })
      } finally {
        setIsSubmitting(false)
      }
    }
  }

  const handlePrevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1)
    }
  }

  const openForm = () => {
    setIsFormOpen(true)
    setCurrentStep(1)
    setIsSubmitted(false)
  }

  return (
    <Box bg="white" color="gray.900">
      {/* ========================================
          HERO SECTION
      ======================================== */}
      <Box
        position="relative"
        overflow="hidden"
        minH="100vh"
        display="flex"
        alignItems="center"
      >
        {/* Brand gradient background */}
        <Box
          position="absolute"
          inset={0}
          bg="radial-gradient(circle at 50% 50%, rgba(6, 95, 70, 0.08), transparent 70%)"
        />
        {/* Grid pattern - brand style */}
        <Box
          position="absolute"
          inset={0}
          bgImage="linear-gradient(to right, rgba(6, 95, 70, 0.05) 1px, transparent 1px), linear-gradient(to bottom, rgba(6, 95, 70, 0.05) 1px, transparent 1px)"
          bgSize="60px 60px"
          opacity={0.5}
        />

        <Container maxW="container.xl" position="relative" zIndex={1} py={{ base: 20, md: 32 }} px={{ base: 6, md: 12, lg: 16 }}>
          <VStack gap={8} textAlign="center" maxW="900px" mx="auto">
            {/* Badge exclusif */}
            <Badge
              bg="brand.100"
              color="brand.700"
              px={4}
              py={2}
              borderRadius="full"
              fontSize="sm"
              fontWeight="semibold"
              border="1px solid"
              borderColor="brand.200"
            >
              <HStack gap={2}>
                <Flame size={16} />
                <Text>Exclusive Program — {placesLeft} places sur {TOTAL_PLACES}</Text>
              </HStack>
            </Badge>

            {/* Main headline */}
            <Heading
              as="h1"
              fontSize={{ base: '4xl', md: '5xl', lg: '6xl' }}
              fontWeight="bold"
              color="gray.900"
              lineHeight="1.1"
              letterSpacing="tight"
            >
              Devenez un Syndic{' '}
              <Text as="span" color="brand.600">
                100% Digital
              </Text>
            </Heading>

            {/* Subtitle */}
            <Text
              fontSize={{ base: 'lg', md: 'xl' }}
              color="gray.600"
              maxW="700px"
              lineHeight="tall"
            >
              Documents, comptabilité, assemblées générales, relation copropriétaires —{' '}
              <Text as="span" color="gray.900" fontWeight="semibold">
                tout digitalisé dans un seul outil moderne.
              </Text>
            </Text>

            <Text fontSize="lg" color="gray.500">
              Rejoignez les {TOTAL_PLACES} pionniers qui transforment leur activité avant tout le monde.
            </Text>

            {/* CTA Button with brand glow effect */}
            <Button
              size="xl"
              px={10}
              py={8}
              fontSize="lg"
              fontWeight="bold"
              colorPalette="brand"
              bg="brand.600"
              color="white"
              _hover={{
                bg: 'brand.700',
                transform: 'translateY(-2px)',
              }}
              boxShadow="0 0 40px rgba(6, 95, 70, 0.3)"
              transition="all 0.2s"
              onClick={openForm}
            >
              Candidater au Founders Program
              <ArrowRight size={20} style={{ marginLeft: '8px' }} />
            </Button>

            {/* Micro-copy */}
            <HStack gap={8} color="gray.500" fontSize="sm" flexWrap="wrap" justify="center">
              <HStack gap={2}>
                <Clock size={14} />
                <Text>Candidature en 3 min</Text>
              </HStack>
              <HStack gap={2}>
                <Zap size={14} />
                <Text>Réponse sous 48h</Text>
              </HStack>
              <HStack gap={2}>
                <Gift size={14} />
                <Text>Accès gratuit jusqu'au lancement</Text>
              </HStack>
            </HStack>

            {/* Places counter */}
            <Box
              mt={8}
              p={6}
              bg="white"
              borderRadius="2xl"
              border="2px solid"
              borderColor="brand.200"
              maxW="400px"
              w="full"
              boxShadow="0 20px 60px -15px rgba(6, 95, 70, 0.15)"
            >
              <HStack justify="space-between" mb={3}>
                <Text color="gray.600" fontSize="sm">Places attribuées</Text>
                <Text color="brand.600" fontWeight="bold" fontSize="lg">
                  {PLACES_TAKEN} / {TOTAL_PLACES}
                </Text>
              </HStack>
              <Box
                h="8px"
                bg="gray.100"
                borderRadius="full"
                overflow="hidden"
              >
                <Box
                  h="full"
                  w={`${progressPercent}%`}
                  bg="brand.600"
                  borderRadius="full"
                  transition="width 1s ease"
                />
              </Box>
              <Text color="gray.500" fontSize="xs" mt={2} textAlign="center">
                Lancement public : T2 2025
              </Text>
            </Box>
          </VStack>
        </Container>

        {/* Scroll indicator */}
        <Box
          position="absolute"
          bottom={8}
          left="50%"
          transform="translateX(-50%)"
        >
          <ChevronDown size={32} color="#9ca3af" />
        </Box>
      </Box>

      {/* ========================================
          PROBLEM SECTION
      ======================================== */}
      <Box py={{ base: 20, md: 32 }} bg="gray.50" position="relative" overflow="hidden">
        {/* Brand gradient background */}
        <Box
          position="absolute"
          inset={0}
          bg="radial-gradient(circle at 50% 50%, rgba(6, 95, 70, 0.08), transparent 70%)"
        />

        <Container maxW="container.xl" px={{ base: 6, md: 12, lg: 16 }} position="relative" zIndex={1}>
          <VStack gap={16}>
            {/* Section header */}
            <VStack gap={4} textAlign="center" maxW="800px">
              <Heading
                as="h2"
                fontSize={{ base: '3xl', md: '4xl', lg: '5xl' }}
                fontWeight="bold"
                color="gray.900"
                lineHeight="1.1"
              >
                Pendant que vous jonglez entre{' '}
                <Text as="span" color="red.500">10 outils</Text>{' '}
                qui ne se parlent pas...
              </Heading>
              <Text fontSize={{ base: 'lg', md: 'xl' }} color="gray.600" lineHeight="tall">
                Excel pour les budgets. Un logiciel archaïque pour la compta. Word pour les convocations.
                Dropbox pour les documents. Emails pour les copropriétaires. Et des exports dans tous les sens
                pour que tout ça communique (un peu).
              </Text>
              <Text fontSize="xl" fontWeight="semibold" color="gray.800" mt={2}>
                Résultat ? Vous passez plus de temps à faire de l'administratif qu'à gérer vos copropriétés.
              </Text>
            </VStack>

            {/* Pain points cards */}
            <Grid
              templateColumns={{ base: '1fr', md: 'repeat(3, 1fr)' }}
              gap={8}
              w="full"
            >
              {painPoints.map((point, index) => (
                <Box
                  key={index}
                  p={8}
                  bg="white"
                  borderRadius="2xl"
                  border="1px solid"
                  borderColor="gray.200"
                  transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
                  _hover={{
                    transform: 'translateY(-8px)',
                    boxShadow: '0 20px 60px -15px rgba(6, 95, 70, 0.2)',
                    borderColor: 'brand.200',
                  }}
                >
                  <VStack align="start" gap={4}>
                    <Box
                      p={3}
                      bg="red.50"
                      borderRadius="xl"
                      color="red.500"
                    >
                      <point.icon size={28} />
                    </Box>
                    <Heading as="h3" fontSize="xl" fontWeight="bold" color="gray.900">
                      {point.title}
                    </Heading>
                    <Text color="gray.600" lineHeight="tall">
                      {point.description}
                    </Text>
                    <Text
                      color="brand.600"
                      fontWeight="semibold"
                      fontStyle="italic"
                      mt={2}
                    >
                      {point.solution}
                    </Text>
                  </VStack>
                </Box>
              ))}
            </Grid>
          </VStack>
        </Container>
      </Box>

      {/* ========================================
          SOLUTION SECTION
      ======================================== */}
      <Box py={{ base: 20, md: 32 }} bg="white" position="relative" overflow="hidden">
        {/* Grid pattern */}
        <Box
          position="absolute"
          inset={0}
          bgImage="linear-gradient(to right, rgba(6, 95, 70, 0.03) 1px, transparent 1px), linear-gradient(to bottom, rgba(6, 95, 70, 0.03) 1px, transparent 1px)"
          bgSize="60px 60px"
          opacity={0.5}
        />

        <Container maxW="container.xl" px={{ base: 6, md: 12, lg: 16 }} position="relative" zIndex={1}>
          <VStack gap={16}>
            {/* Section header */}
            <VStack gap={4} textAlign="center" maxW="700px">
              <Badge
                bg="brand.100"
                color="brand.700"
                px={4}
                py={2}
                borderRadius="full"
                fontSize="sm"
                fontWeight="semibold"
                border="1px solid"
                borderColor="brand.200"
              >
                <HStack gap={2}>
                  <Sparkles size={16} />
                  <Text>La Solution</Text>
                </HStack>
              </Badge>
              <Heading
                as="h2"
                fontSize={{ base: '3xl', md: '4xl', lg: '5xl' }}
                fontWeight="bold"
                color="gray.900"
                lineHeight="1.1"
              >
                KOPRO : Tout digitalisé.{' '}
                <Text as="span" color="brand.600">Tout au même endroit.</Text>
              </Heading>
              <Text fontSize={{ base: 'lg', md: 'xl' }} color="gray.600" lineHeight="tall">
                Pas 10 outils. <strong>Un seul.</strong> Moderne, connecté, pensé pour votre métier.
              </Text>
            </VStack>

            {/* Solution pillars */}
            <Grid
              templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }}
              gap={8}
              w="full"
            >
              {solutionPillars.map((pillar, index) => (
                <Box
                  key={index}
                  p={8}
                  bg="white"
                  borderRadius="2xl"
                  border="2px solid"
                  borderColor="gray.100"
                  transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
                  _hover={{
                    transform: 'translateY(-4px)',
                    borderColor: 'brand.200',
                    boxShadow: '0 20px 60px -15px rgba(6, 95, 70, 0.15)',
                  }}
                >
                  <VStack align="start" gap={5}>
                    <HStack gap={4}>
                      <Box
                        p={4}
                        bg="brand.50"
                        borderRadius="xl"
                        color="brand.600"
                      >
                        <pillar.icon size={32} />
                      </Box>
                      <Heading as="h3" fontSize="xl" fontWeight="bold" color="gray.900">
                        {pillar.title}
                      </Heading>
                    </HStack>
                    <Text color="gray.600" fontSize="md" lineHeight="tall">
                      {pillar.description}
                    </Text>
                    <VStack align="start" gap={2} w="full">
                      {pillar.features.map((feature, idx) => (
                        <HStack key={idx} gap={3}>
                          <CheckCircle size={18} color="#065F46" />
                          <Text color="gray.700" fontSize="sm">{feature}</Text>
                        </HStack>
                      ))}
                    </VStack>
                  </VStack>
                </Box>
              ))}
            </Grid>

            {/* Result callout */}
            <Box
              p={8}
              bg="brand.50"
              borderRadius="2xl"
              border="1px solid"
              borderColor="brand.100"
              maxW="800px"
              textAlign="center"
            >
              <HStack justify="center" gap={2} mb={3}>
                <Sparkles size={24} color="#065F46" />
                <Text fontSize="lg" fontWeight="bold" color="brand.700">
                  Le résultat ?
                </Text>
              </HStack>
              <Text fontSize="xl" color="gray.700" lineHeight="tall">
                Vous ne jonglez plus. Vous ne ressaisissez plus. Vous ne cherchez plus.
              </Text>
              <Text fontSize="2xl" fontWeight="bold" color="gray.900" mt={2}>
                Vous gérez. Et vous avez enfin le temps de le faire bien.
              </Text>
            </Box>
          </VStack>
        </Container>
      </Box>

      {/* ========================================
          FOUNDER PROGRAM SECTION
      ======================================== */}
      <Box py={{ base: 20, md: 32 }} bg="brand.700" position="relative" overflow="hidden">
        {/* Subtle pattern overlay */}
        <Box
          position="absolute"
          inset={0}
          bgImage="linear-gradient(to right, rgba(255, 255, 255, 0.02) 1px, transparent 1px), linear-gradient(to bottom, rgba(255, 255, 255, 0.02) 1px, transparent 1px)"
          bgSize="60px 60px"
        />
        {/* Radial glow */}
        <Box
          position="absolute"
          inset={0}
          bg="radial-gradient(circle at 50% 0%, rgba(255, 255, 255, 0.1), transparent 50%)"
        />

        <Container maxW="container.xl" px={{ base: 6, md: 12, lg: 16 }} position="relative" zIndex={1}>
          <VStack gap={16}>
            {/* Section header */}
            <VStack gap={4} textAlign="center" maxW="800px">
              <Badge
                bg="white/10"
                color="white"
                px={4}
                py={2}
                borderRadius="full"
                fontSize="sm"
                fontWeight="semibold"
                border="1px solid"
                borderColor="white/20"
              >
                Founders Program
              </Badge>
              <Heading
                as="h2"
                fontSize={{ base: '3xl', md: '4xl', lg: '5xl' }}
                fontWeight="bold"
                color="white"
                lineHeight="1.1"
              >
                Le Founders Program
              </Heading>
              <Text fontSize={{ base: 'xl', md: '2xl' }} color="brand.200" fontWeight="semibold">
                Digitalisez votre activité AVANT tout le monde
              </Text>
              <Text fontSize="lg" color="white/80" lineHeight="tall" mt={4}>
                KOPRO ouvre au public au T2 2025. Mais pour {TOTAL_PLACES} professionnels sélectionnés,
                la digitalisation commence <strong style={{ color: 'white' }}>maintenant</strong>.
              </Text>
              <Text fontSize="md" color="white/60" lineHeight="tall">
                En rejoignant le Founders Program, vous ne "testez" pas un produit. Vous{' '}
                <strong style={{ color: 'white' }}>transformez votre façon de travailler</strong> —
                et vous nous aidez à créer l'outil parfait pour votre métier.
              </Text>
            </VStack>

            {/* Two columns: Advantages and Commitments */}
            <Grid
              templateColumns={{ base: '1fr', lg: 'repeat(2, 1fr)' }}
              gap={12}
              w="full"
            >
              {/* What you get */}
              <Box>
                <HStack gap={3} mb={6}>
                  <Gift size={24} color="#7ff0d0" />
                  <Heading as="h3" fontSize="xl" fontWeight="bold" color="white">
                    Ce que vous gagnez
                  </Heading>
                </HStack>
                <VStack gap={4} align="stretch">
                  {founderAdvantages.map((advantage, index) => (
                    <HStack
                      key={index}
                      p={4}
                      bg="white/5"
                      borderRadius="xl"
                      border="1px solid"
                      borderColor="white/10"
                      gap={4}
                      transition="all 0.2s"
                      _hover={{ bg: 'white/10', borderColor: 'white/20' }}
                    >
                      <Box
                        p={2}
                        bg="brand.400/20"
                        borderRadius="lg"
                        color="brand.300"
                      >
                        <advantage.icon size={20} />
                      </Box>
                      <VStack align="start" gap={0}>
                        <Text color="white" fontWeight="semibold">
                          {advantage.title}
                        </Text>
                        <Text color="white/60" fontSize="sm">
                          {advantage.description}
                        </Text>
                      </VStack>
                    </HStack>
                  ))}
                </VStack>
              </Box>

              {/* What we ask */}
              <Box>
                <HStack gap={3} mb={6}>
                  <Users size={24} color="#fcd34d" />
                  <Heading as="h3" fontSize="xl" fontWeight="bold" color="white">
                    Ce qu'on vous demande
                  </Heading>
                </HStack>
                <VStack gap={4} align="stretch">
                  {founderCommitments.map((commitment, index) => (
                    <HStack
                      key={index}
                      p={4}
                      bg="white/5"
                      borderRadius="xl"
                      border="1px solid"
                      borderColor="white/10"
                      gap={4}
                      transition="all 0.2s"
                      _hover={{ bg: 'white/10', borderColor: 'white/20' }}
                    >
                      <Box
                        p={2}
                        bg="yellow.500/20"
                        borderRadius="lg"
                        color="yellow.300"
                      >
                        <commitment.icon size={20} />
                      </Box>
                      <VStack align="start" gap={0}>
                        <Text color="white" fontWeight="semibold">
                          {commitment.title}
                        </Text>
                        <Text color="white/60" fontSize="sm">
                          {commitment.description}
                        </Text>
                      </VStack>
                    </HStack>
                  ))}
                </VStack>
              </Box>
            </Grid>

            {/* Transparency callout */}
            <Box
              p={8}
              bg="white/5"
              borderRadius="2xl"
              border="1px solid"
              borderColor="white/10"
              maxW="800px"
            >
              <HStack gap={3} mb={4}>
                <Sparkles size={24} color="#7ff0d0" />
                <Text fontSize="lg" fontWeight="bold" color="brand.300">
                  Pourquoi on fait ça ?
                </Text>
              </HStack>
              <Text color="white/80" lineHeight="tall" mb={4}>
                KOPRO est construit avec une connaissance approfondie du métier de syndic. Mais rien ne remplace la réalité du terrain.
              </Text>
              <Text color="white/80" lineHeight="tall" mb={4}>
                Vos retours ne sont pas des "nice to have". Ils façonnent directement le produit.
                Quand quelque chose ne colle pas à votre façon de travailler, c'est ajusté.
              </Text>
              <Text color="white" fontWeight="semibold" fontSize="lg">
                C'est un vrai partenariat. Vous digitalisez votre activité, ensemble nous créons l'outil parfait pour le métier.
              </Text>
            </Box>
          </VStack>
        </Container>
      </Box>

      {/* ========================================
          TIMELINE SECTION
      ======================================== */}
      <Box py={{ base: 20, md: 32 }} bg="gray.50" position="relative" overflow="hidden">
        {/* Gradient */}
        <Box
          position="absolute"
          inset={0}
          bg="radial-gradient(circle at 50% 50%, rgba(6, 95, 70, 0.06), transparent 70%)"
        />

        <Container maxW="container.xl" px={{ base: 6, md: 12, lg: 16 }} position="relative" zIndex={1}>
          <VStack gap={16}>
            {/* Section header */}
            <VStack gap={4} textAlign="center">
              <Heading
                as="h2"
                fontSize={{ base: '3xl', md: '4xl', lg: '5xl' }}
                fontWeight="bold"
                color="gray.900"
                lineHeight="1.1"
              >
                Votre parcours vers le{' '}
                <Text as="span" color="brand.600">100% digital</Text>
              </Heading>
            </VStack>

            {/* Timeline */}
            <Box w="full" maxW="900px" mx="auto">
              <VStack gap={0} align="stretch">
                {timelineSteps.map((step, index) => (
                  <HStack
                    key={step.step}
                    gap={6}
                    position="relative"
                    pb={index < timelineSteps.length - 1 ? 8 : 0}
                  >
                    {/* Timeline line */}
                    {index < timelineSteps.length - 1 && (
                      <Box
                        position="absolute"
                        left="24px"
                        top="48px"
                        bottom="0"
                        w="2px"
                        bg="brand.200"
                      />
                    )}

                    {/* Step number */}
                    <Box
                      w="50px"
                      h="50px"
                      borderRadius="full"
                      bg="brand.600"
                      color="white"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      fontWeight="bold"
                      fontSize="xl"
                      flexShrink={0}
                      zIndex={1}
                    >
                      {step.step}
                    </Box>

                    {/* Content */}
                    <Box
                      flex={1}
                      p={6}
                      bg="white"
                      borderRadius="xl"
                      border="1px solid"
                      borderColor="gray.200"
                      transition="all 0.2s"
                      _hover={{ borderColor: 'brand.200', boxShadow: '0 10px 40px -10px rgba(6, 95, 70, 0.1)' }}
                    >
                      <HStack justify="space-between" mb={2} flexWrap="wrap" gap={2}>
                        <Heading as="h3" fontSize="lg" fontWeight="bold" color="gray.900">
                          {step.title}
                        </Heading>
                        <Badge
                          bg="brand.50"
                          color="brand.700"
                          px={3}
                          py={1}
                          borderRadius="full"
                          fontSize="xs"
                        >
                          {step.duration}
                        </Badge>
                      </HStack>
                      <Text color="gray.600" lineHeight="tall">
                        {step.description}
                      </Text>
                    </Box>
                  </HStack>
                ))}
              </VStack>
            </Box>
          </VStack>
        </Container>
      </Box>

      {/* ========================================
          FAQ SECTION
      ======================================== */}
      <Box py={{ base: 20, md: 32 }} bg="white">
        <Container maxW="container.lg" px={{ base: 6, md: 12, lg: 16 }}>
          <VStack gap={12}>
            {/* Section header */}
            <Heading
              as="h2"
              fontSize={{ base: '3xl', md: '4xl' }}
              fontWeight="bold"
              color="gray.900"
              textAlign="center"
            >
              Questions fréquentes
            </Heading>

            {/* FAQ Accordion */}
            <AccordionRoot collapsible w="full">
              {faqItems.map((item, index) => (
                <AccordionItem key={index} value={`item-${index}`} borderColor="gray.200">
                  <AccordionItemTrigger
                    py={6}
                    _hover={{ bg: 'gray.50' }}
                    cursor="pointer"
                  >
                    <Text fontSize="lg" fontWeight="600" color="gray.900" textAlign="left" flex={1}>
                      {item.question}
                    </Text>
                  </AccordionItemTrigger>
                  <AccordionItemContent pb={6}>
                    <Text color="gray.600" lineHeight="tall">
                      {item.answer}
                    </Text>
                  </AccordionItemContent>
                </AccordionItem>
              ))}
            </AccordionRoot>
          </VStack>
        </Container>
      </Box>

      {/* ========================================
          CTA FINAL SECTION
      ======================================== */}
      <Box py={{ base: 20, md: 32 }} bg="brand.600" position="relative" overflow="hidden">
        {/* Subtle pattern */}
        <Box
          position="absolute"
          inset={0}
          bgImage="linear-gradient(to right, rgba(255, 255, 255, 0.03) 1px, transparent 1px), linear-gradient(to bottom, rgba(255, 255, 255, 0.03) 1px, transparent 1px)"
          bgSize="60px 60px"
        />
        {/* Radial glow */}
        <Box
          position="absolute"
          inset={0}
          bg="radial-gradient(circle at 50% 100%, rgba(255, 255, 255, 0.15), transparent 50%)"
        />

        <Container maxW="container.lg" px={{ base: 6, md: 12, lg: 16 }} position="relative" zIndex={1}>
          <VStack gap={8} textAlign="center">
            <Heading
              as="h2"
              fontSize={{ base: '3xl', md: '4xl', lg: '5xl' }}
              fontWeight="bold"
              color="white"
              lineHeight="1.1"
            >
              Prêt à passer au{' '}
              <Text as="span" color="brand.200">100% digital</Text> ?
            </Heading>

            <Text fontSize="xl" color="white/80" maxW="600px">
              {TOTAL_PLACES} places. Un accès anticipé. La possibilité de façonner l'outil.
            </Text>
            <Text fontSize="lg" color="white/60">
              Pendant que les autres attendront la sortie publique, vous serez déjà opérationnel.
            </Text>

            <Button
              size="xl"
              px={12}
              py={8}
              fontSize="xl"
              fontWeight="bold"
              bg="white"
              color="brand.700"
              _hover={{
                bg: 'brand.50',
                transform: 'translateY(-2px)',
              }}
              boxShadow="0 20px 60px -15px rgba(0, 0, 0, 0.3)"
              transition="all 0.2s"
              onClick={openForm}
            >
              Candidater au Founders Program
              <ArrowRight size={24} style={{ marginLeft: '8px' }} />
            </Button>

            <HStack gap={8} color="white/70" fontSize="sm" flexWrap="wrap" justify="center" mt={4}>
              <HStack gap={2}>
                <Flame size={16} color="#fcd34d" />
                <Text>Plus que <strong style={{ color: 'white' }}>{placesLeft} places</strong> sur {TOTAL_PLACES}</Text>
              </HStack>
              <HStack gap={2}>
                <Calendar size={16} />
                <Text>Lancement public : T2 2025</Text>
              </HStack>
              <HStack gap={2}>
                <Clock size={16} />
                <Text>Réponse sous 48h</Text>
              </HStack>
            </HStack>
          </VStack>
        </Container>
      </Box>

      {/* ========================================
          APPLICATION FORM (Modal)
      ======================================== */}
      {isFormOpen && (
        <Box
          ref={formRef}
          position="fixed"
          top={0}
          left={0}
          right={0}
          bottom={0}
          bg="rgba(0,0,0,0.6)"
          zIndex={1000}
          display="flex"
          alignItems="center"
          justifyContent="center"
          p={4}
          overflowY="auto"
        >
          <Box
            bg="white"
            borderRadius="2xl"
            maxW="600px"
            w="full"
            maxH="90vh"
            overflowY="auto"
            position="relative"
            boxShadow="0 20px 60px -15px rgba(0, 0, 0, 0.3)"
          >
            {/* Close button */}
            <Button
              position="absolute"
              top={4}
              right={4}
              size="sm"
              variant="ghost"
              onClick={() => setIsFormOpen(false)}
              zIndex={10}
            >
              ✕
            </Button>

            {!isSubmitted ? (
              <Box p={8}>
                {/* Form header */}
                <VStack gap={4} mb={8}>
                  <Heading as="h3" fontSize="2xl" fontWeight="bold" color="gray.900">
                    Candidature Founders Program
                  </Heading>

                  {/* Progress indicator */}
                  <HStack gap={2} w="full" justify="center">
                    {formSteps.map((step) => (
                      <Box
                        key={step.id}
                        w="60px"
                        h="4px"
                        borderRadius="full"
                        bg={step.id <= currentStep ? 'brand.600' : 'gray.200'}
                        transition="all 0.3s"
                      />
                    ))}
                  </HStack>
                  <Text color="gray.500" fontSize="sm">
                    Étape {currentStep} sur 4 : {formSteps[currentStep - 1]?.title}
                  </Text>
                </VStack>

                {/* Form content */}
                <VStack gap={5} align="stretch">
                  {/* Step 1: Personal info */}
                  {currentStep === 1 && (
                    <>
                      <Grid templateColumns={{ base: '1fr', sm: 'repeat(2, 1fr)' }} gap={4}>
                        <Field label="Prénom" required>
                          <Input
                            value={formData.firstName}
                            onChange={(e) => handleInputChange('firstName', e.target.value)}
                            placeholder="Jean"
                          />
                        </Field>
                        <Field label="Nom" required>
                          <Input
                            value={formData.lastName}
                            onChange={(e) => handleInputChange('lastName', e.target.value)}
                            placeholder="Dupont"
                          />
                        </Field>
                      </Grid>
                      <Field label="Email professionnel" required>
                        <Input
                          type="email"
                          value={formData.email}
                          onChange={(e) => handleInputChange('email', e.target.value)}
                          placeholder="jean.dupont@monsyndic.fr"
                        />
                      </Field>
                      <Field label="Téléphone" required>
                        <Input
                          type="tel"
                          value={formData.phone}
                          onChange={(e) => handleInputChange('phone', e.target.value)}
                          placeholder="06 12 34 56 78"
                        />
                      </Field>
                      <Field label="Votre fonction" required>
                        <NativeSelectRoot>
                          <NativeSelectField
                            value={formData.role}
                            onChange={(e) => handleInputChange('role', e.target.value)}
                          >
                            <option value="">Sélectionner...</option>
                            <option value="syndic_pro">Syndic professionnel</option>
                            <option value="conseil_syndical">Conseil syndical</option>
                            <option value="gestionnaire">Gestionnaire</option>
                            <option value="autre">Autre</option>
                          </NativeSelectField>
                        </NativeSelectRoot>
                      </Field>
                    </>
                  )}

                  {/* Step 2: Activity info */}
                  {currentStep === 2 && (
                    <>
                      <Field label="Nom de votre structure/copropriété" required>
                        <Input
                          value={formData.company}
                          onChange={(e) => handleInputChange('company', e.target.value)}
                          placeholder="Cabinet Immobilier Dupont / Résidence Les Lilas"
                        />
                      </Field>
                      <Field label="Nombre de copropriétés gérées" required>
                        <NativeSelectRoot>
                          <NativeSelectField
                            value={formData.coprCount}
                            onChange={(e) => handleInputChange('coprCount', e.target.value)}
                          >
                            <option value="">Sélectionner...</option>
                            <option value="1-5">1 à 5</option>
                            <option value="6-20">6 à 20</option>
                            <option value="21-50">21 à 50</option>
                            <option value="50+">Plus de 50</option>
                          </NativeSelectField>
                        </NativeSelectRoot>
                      </Field>
                      <Field label="Nombre total de lots" required>
                        <NativeSelectRoot>
                          <NativeSelectField
                            value={formData.lotsCount}
                            onChange={(e) => handleInputChange('lotsCount', e.target.value)}
                          >
                            <option value="">Sélectionner...</option>
                            <option value="<50">Moins de 50</option>
                            <option value="50-200">50 à 200</option>
                            <option value="200-500">200 à 500</option>
                            <option value="500+">Plus de 500</option>
                          </NativeSelectField>
                        </NativeSelectRoot>
                      </Field>
                      <Field label="Outil(s) actuel(s)" helperText="Sélectionner le principal">
                        <NativeSelectRoot>
                          <NativeSelectField
                            value={formData.currentTools[0] || ''}
                            onChange={(e) => handleInputChange('currentTools', [e.target.value])}
                          >
                            <option value="">Sélectionner...</option>
                            <option value="excel">Excel</option>
                            <option value="crypto">Crypto (CDC Habitat)</option>
                            <option value="ics">ICS</option>
                            <option value="spi">SPI</option>
                            <option value="cegid">Cegid</option>
                            <option value="autre">Autre logiciel</option>
                            <option value="papier">Aucun (papier)</option>
                          </NativeSelectField>
                        </NativeSelectRoot>
                      </Field>
                    </>
                  )}

                  {/* Step 3: Digitalization */}
                  {currentStep === 3 && (
                    <>
                      <Field
                        label="Qu'est-ce qui vous empêche aujourd'hui d'être 100% digital ?"
                        required
                        helperText="Minimum 150 caractères"
                      >
                        <Textarea
                          value={formData.blockers}
                          onChange={(e) => handleInputChange('blockers', e.target.value)}
                          placeholder="Décrivez les obstacles que vous rencontrez..."
                          rows={4}
                        />
                      </Field>
                      <Field
                        label="Quelle tâche aimeriez-vous ne plus jamais faire manuellement ?"
                        required
                        helperText="Minimum 100 caractères"
                      >
                        <Textarea
                          value={formData.wishToAutomate}
                          onChange={(e) => handleInputChange('wishToAutomate', e.target.value)}
                          placeholder="La tâche qui vous fait perdre le plus de temps..."
                          rows={3}
                        />
                      </Field>
                      <Field label="Avez-vous une AG prévue dans les 6 prochains mois ?" required>
                        <NativeSelectRoot>
                          <NativeSelectField
                            value={formData.agPlanned}
                            onChange={(e) => handleInputChange('agPlanned', e.target.value)}
                          >
                            <option value="">Sélectionner...</option>
                            <option value="<3months">Oui, dans moins de 3 mois</option>
                            <option value="3-6months">Oui, dans 3 à 6 mois</option>
                            <option value="no">Non</option>
                            <option value="unknown">Je ne sais pas</option>
                          </NativeSelectField>
                        </NativeSelectRoot>
                      </Field>
                      <Field label="Êtes-vous décisionnaire pour le choix des outils ?" required>
                        <NativeSelectRoot>
                          <NativeSelectField
                            value={formData.isDecider}
                            onChange={(e) => handleInputChange('isDecider', e.target.value)}
                          >
                            <option value="">Sélectionner...</option>
                            <option value="yes_alone">Oui, seul(e)</option>
                            <option value="yes_with_others">Oui, avec d'autres</option>
                            <option value="influencer">Je suis influenceur</option>
                            <option value="no">Non</option>
                          </NativeSelectField>
                        </NativeSelectRoot>
                      </Field>
                    </>
                  )}

                  {/* Step 4: Motivation */}
                  {currentStep === 4 && (
                    <>
                      <Field
                        label="Pourquoi voulez-vous digitaliser votre activité maintenant ?"
                        required
                        helperText="Minimum 150 caractères"
                      >
                        <Textarea
                          value={formData.whyNow}
                          onChange={(e) => handleInputChange('whyNow', e.target.value)}
                          placeholder="Qu'est-ce qui vous motive à franchir le pas maintenant..."
                          rows={4}
                        />
                      </Field>
                      <Field label="Combien de temps pouvez-vous consacrer par semaine (hors usage normal) ?" required>
                        <NativeSelectRoot>
                          <NativeSelectField
                            value={formData.timeAvailable}
                            onChange={(e) => handleInputChange('timeAvailable', e.target.value)}
                          >
                            <option value="">Sélectionner...</option>
                            <option value="30min">Environ 30 min</option>
                            <option value="1h">Environ 1h</option>
                            <option value="1h+">Plus d'1h</option>
                            <option value="unsure">Pas sûr(e)</option>
                          </NativeSelectField>
                        </NativeSelectRoot>
                      </Field>
                      <Field label="Comment avez-vous découvert KOPRO ?">
                        <NativeSelectRoot>
                          <NativeSelectField
                            value={formData.source}
                            onChange={(e) => handleInputChange('source', e.target.value)}
                          >
                            <option value="">Sélectionner...</option>
                            <option value="linkedin">LinkedIn</option>
                            <option value="word_of_mouth">Bouche à oreille</option>
                            <option value="google">Recherche Google</option>
                            <option value="founder_invite">Invitation Founder</option>
                            <option value="other">Autre</option>
                          </NativeSelectField>
                        </NativeSelectRoot>
                      </Field>
                      {formData.source === 'founder_invite' && (
                        <Field label="Si invité par un Founder, son nom">
                          <Input
                            value={formData.referrer}
                            onChange={(e) => handleInputChange('referrer', e.target.value)}
                            placeholder="Nom du Founder"
                          />
                        </Field>
                      )}
                    </>
                  )}
                </VStack>

                {/* Form navigation */}
                <HStack justify="space-between" mt={8}>
                  {currentStep > 1 ? (
                    <Button
                      variant="ghost"
                      onClick={handlePrevStep}
                    >
                      <ArrowLeft size={18} style={{ marginRight: '8px' }} />
                      Retour
                    </Button>
                  ) : (
                    <Box />
                  )}
                  <Button
                    colorPalette="brand"
                    bg="brand.600"
                    color="white"
                    _hover={{ bg: 'brand.700' }}
                    onClick={handleNextStep}
                    px={8}
                    loading={isSubmitting}
                    disabled={isSubmitting}
                  >
                    {currentStep === 4 ? (isSubmitting ? 'Envoi en cours...' : 'Envoyer ma candidature') : 'Suivant'}
                    {!isSubmitting && (currentStep === 4 ? (
                      <Send size={18} style={{ marginLeft: '8px' }} />
                    ) : (
                      <ArrowRight size={18} style={{ marginLeft: '8px' }} />
                    ))}
                  </Button>
                </HStack>
              </Box>
            ) : (
              /* Confirmation message */
              <Box p={12} textAlign="center">
                <VStack gap={6}>
                  <Box
                    w="80px"
                    h="80px"
                    borderRadius="full"
                    bg="brand.50"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <Check size={40} color="#065F46" />
                  </Box>
                  <Heading as="h3" fontSize="2xl" fontWeight="bold" color="gray.900">
                    Candidature envoyée !
                  </Heading>
                  <Text color="gray.600" lineHeight="tall" maxW="400px">
                    Merci pour votre intérêt pour le Founders Program !
                  </Text>
                  <Text color="gray.600" lineHeight="tall" maxW="400px">
                    <strong>Prochaine étape :</strong> Nous examinons chaque candidature personnellement.
                    Réponse par email sous 48h.
                  </Text>
                  <VStack gap={3} mt={4}>
                    <Text color="gray.500" fontSize="sm">D'ici là, vous pouvez :</Text>
                    <HStack gap={4}>
                      <a href="https://linkedin.com/company/kopro" target="_blank" rel="noopener noreferrer">
                        <Button variant="outline" size="sm" borderColor="gray.300">
                          Suivre sur LinkedIn
                        </Button>
                      </a>
                    </HStack>
                  </VStack>
                  <Text color="gray.700" fontWeight="semibold" mt={4}>
                    À très vite dans l'aventure 100% digital !
                  </Text>
                  <Text color="gray.500" fontStyle="italic">
                    L'équipe KOPRO
                  </Text>
                  <Button
                    mt={4}
                    variant="ghost"
                    onClick={() => setIsFormOpen(false)}
                  >
                    Fermer
                  </Button>
                </VStack>
              </Box>
            )}
          </Box>
        </Box>
      )}

      {/* ========================================
          FOOTER
      ======================================== */}
      <Box py={8} bg="gray.900" borderTop="1px solid" borderColor="gray.800">
        <Container maxW="container.xl" px={{ base: 6, md: 12, lg: 16 }}>
          <HStack justify="space-between" flexWrap="wrap" gap={4}>
            <HStack gap={4}>
              <RouterLink to="/">
                <img
                  src="/assets/logo-inline-dark.svg"
                  alt="Kopro"
                  style={{ height: '30px' }}
                />
              </RouterLink>
              <Text color="gray.500" fontSize="sm">
                © 2025 KOPRO. Tous droits réservés.
              </Text>
            </HStack>
            <HStack gap={6}>
              <RouterLink to="/">
                <Text color="gray.400" fontSize="sm" _hover={{ color: 'white' }}>
                  Accueil
                </Text>
              </RouterLink>
              <RouterLink to="/contact">
                <Text color="gray.400" fontSize="sm" _hover={{ color: 'white' }}>
                  Contact
                </Text>
              </RouterLink>
            </HStack>
          </HStack>
        </Container>
      </Box>
    </Box>
  )
}
